// Legend for Home Stock level jar icons (title row on review / latest home stock).

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { INVENTORY_LEVEL } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { MAIN_APP_SCROLL_SELECTOR } from '../../lib/mainAppScroll';
import { HomeStockLevelIcon } from './HomeStockLevelIcon';

const LEGEND_LEVELS = [
  INVENTORY_LEVEL.FULL,
  INVENTORY_LEVEL.HALF,
  INVENTORY_LEVEL.LOW,
  INVENTORY_LEVEL.EMPTY,
] as const;

type LegendLevel = (typeof LEGEND_LEVELS)[number];

const legendLabelKey = (level: LegendLevel): string => `task.review.stockLevelLegend.${level}`;

/** Below AppTopBar (z-20) and pinned Location strip (z-[15]). */
const OPEN_STACK_CLASS = 'z-[14]';

export const HomeStockLevelLegend = () => {
  const { t } = useLanguage();
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback((): void => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleClose, handleToggle],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [handleClose, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleScrollClose = (): void => {
      handleClose();
    };
    const mainAppScroll = document.querySelector(MAIN_APP_SCROLL_SELECTOR);
    mainAppScroll?.addEventListener('scroll', handleScrollClose, { passive: true });
    window.addEventListener('scroll', handleScrollClose, { passive: true });
    return () => {
      mainAppScroll?.removeEventListener('scroll', handleScrollClose);
      window.removeEventListener('scroll', handleScrollClose);
    };
  }, [handleClose, open]);

  return (
    <div ref={rootRef} className={`relative shrink-0 ${open ? OPEN_STACK_CLASS : ''}`}>
      <button
        type="button"
        className="flex items-center gap-1.5 rounded-full border border-gray-200/80 bg-white px-3 py-1.5 text-gray-500 shadow-sm transition-colors hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-emerald-300"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('task.review.stockLevelLegendButtonAria')}
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
      >
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-[10px] font-bold uppercase tracking-wider">{t('task.review.stockLevelLegendButton')}</span>
      </button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={t('task.review.stockLevelLegendPanelAria')}
          className="absolute right-0 top-full z-10 mt-2 flex w-56 flex-col gap-4 rounded-2xl border border-gray-200/80 bg-white p-4 shadow-xl dark:border-gray-600 dark:bg-gray-900"
        >
          {LEGEND_LEVELS.map((level) => (
            <div key={level} className="flex items-center gap-4">
              <HomeStockLevelIcon
                level={level}
                size="md"
                ariaLabel={t(legendLabelKey(level))}
                blinkWhenEmpty={level === INVENTORY_LEVEL.EMPTY}
              />
              <span className="text-xs font-bold text-gray-900 dark:text-gray-50">{t(legendLabelKey(level))}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
